const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Temporary user ID for development (replace with real auth later)
const USER_ID = 'dev-user-' + Math.random().toString(36).substring(7);

class ApiClient {
  private async request(endpoint: string, options: RequestInit = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // Templates
  async getTemplates(params?: { category?: string; limit?: number; offset?: number }) {
    const query = new URLSearchParams();
    if (params?.category) query.append('category', params.category);
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.offset) query.append('offset', params.offset.toString());
    
    return this.request(`/templates?${query}`);
  }

  async getTemplate(id: string) {
    return this.request(`/templates/${id}`);
  }

  async uploadTemplate(data: {
    name: string;
    description?: string;
    image_data: string;
    width: number;
    height: number;
  }) {
    return this.request('/templates', {
      method: 'POST',
      body: JSON.stringify({ ...data, user_id: USER_ID }),
    });
  }

  // Projects
  async getProjects(params?: { limit?: number; offset?: number }) {
    const query = new URLSearchParams({ user_id: USER_ID });
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.offset) query.append('offset', params.offset.toString());
    
    return this.request(`/projects?${query}`);
  }

  async getProject(id: string) {
    const query = new URLSearchParams({ user_id: USER_ID });
    return this.request(`/projects/${id}?${query}`);
  }

  async createProject(data: {
    name: string;
    template_id?: string;
    canvas_state: any;
  }) {
    return this.request('/projects', {
      method: 'POST',
      body: JSON.stringify({ ...data, user_id: USER_ID }),
    });
  }

  async updateProject(id: string, data: {
    name?: string;
    canvas_state?: any;
    thumbnail_url?: string;
  }) {
    return this.request(`/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...data, user_id: USER_ID }),
    });
  }

  async deleteProject(id: string) {
    const query = new URLSearchParams({ user_id: USER_ID });
    return this.request(`/projects/${id}?${query}`, {
      method: 'DELETE',
    });
  }

  // Uploads
  async uploadImage(data: {
    file_data: string;
    file_name?: string;
    file_type?: string;
  }) {
    return this.request('/upload', {
      method: 'POST',
      body: JSON.stringify({ ...data, user_id: USER_ID }),
    });
  }

  async getAssets(params?: { limit?: number; offset?: number }) {
    const query = new URLSearchParams({ user_id: USER_ID });
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.offset) query.append('offset', params.offset.toString());
    
    return this.request(`/upload/assets?${query}`);
  }

  // Export
  async exportMeme(data: {
    project_id?: string;
    image_data: string;
    format?: 'png' | 'jpg';
    quality?: number;
  }) {
    return this.request('/export', {
      method: 'POST',
      body: JSON.stringify({ ...data, user_id: USER_ID }),
    });
  }

  async getExportHistory(params?: { limit?: number; offset?: number }) {
    const query = new URLSearchParams({ user_id: USER_ID });
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.offset) query.append('offset', params.offset.toString());
    
    return this.request(`/export/history?${query}`);
  }

  // AI Features
  async suggestCaptions(data: {
    template_id: string;
    context?: string;
    style?: string;
  }) {
    return this.request('/ai/suggest-caption', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async removeBackground(imageData: string) {
    return this.request('/ai/remove-bg', {
      method: 'POST',
      body: JSON.stringify({ image_data: imageData }),
    });
  }

  getUserId() {
    return USER_ID;
  }
}

export const api = new ApiClient();
